import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControlLabel, Grid, IconButton, InputAdornment, LinearProgress,
  Link, Stack, Step, StepLabel, Stepper, Switch, TextField, Typography,
} from '@mui/material';
import {
  Business, CheckCircle, Email, Person, Lock, Visibility, VisibilityOff,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || '';
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || '';
const USER_POOL_CLIENT_ID = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';
const APP_URL = process.env.REACT_APP_URL || window.location.origin;
const STEPS = ['Your account', 'Verify email', 'Set up workspace', 'You\'re in!'];

const COMPANY_SIZES = [
  { value: '1-10',    label: '1–10 employees' },
  { value: '11-50',   label: '11–50 employees' },
  { value: '51-250',  label: '51–250 employees' },
  { value: '250+',    label: '250+ employees' },
];

export default function SignupPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [socialProviders, setSocialProviders] = useState([]);

  // Fetch enabled social login providers on mount
  useEffect(() => {
    fetch(`${API_BASE}/signup/social-providers`)
      .then(r => r.json())
      .then(data => setSocialProviders(data.providers || []))
      .catch(() => setSocialProviders([]));
  }, []);

  // Form state
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    password: '', companyName: '', companySize: '',
    seedData: true,
  });
  const [otp, setOtp] = useState('');

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setError('');
  };

  // ── Step 0: Account details ────────────────────────────────────────────────
  async function handleSubmitAccount() {
    const { firstName, email, companyName, password } = form;
    if (!firstName || !email || !companyName) {
      setError('First name, work email, and company name are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (password && password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim(),
          email:     form.email.trim().toLowerCase(),
          companyName: form.companyName.trim(),
          companySize: form.companySize,
          seedData: form.seedData,
        }),
      });
      const data = await res.json();
      if (!res.ok) { throw new Error(data.error || 'Request failed'); }
      setStep(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 1: OTP verification ───────────────────────────────────────────────
  async function handleVerifyOTP() {
    if (!otp || otp.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/signup/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:    form.email.trim().toLowerCase(),
          otp:      otp.trim(),
          password: form.password || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { throw new Error(data.error || 'Verification failed'); }
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResendOTP() {
    setLoading(true);
    setError('');
    try {
      await fetch(`${API_BASE}/signup/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim().toLowerCase() }),
      });
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: Set up workspace (seed data confirmation) ─────────────────────
  function handleWorkspaceNext() {
    setStep(3);
  }

  // ── Step 3: Welcome ────────────────────────────────────────────────────────
  function handleGoToApp() {
    window.location.href = '/login';
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" mb={3}>
        <Business sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h5" fontWeight={700} color="primary">
          Inventory Platform
        </Typography>
      </Stack>

      {/* Progress stepper */}
      <Box sx={{ width: '100%', maxWidth: 560, mb: 3 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Card sx={{ width: '100%', maxWidth: 560, borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        {loading && <LinearProgress />}
        <CardContent sx={{ p: 4 }}>

          {/* ── Step 0 — Account details ── */}
          {step === 0 && (
            <Box>
              <Typography variant="h6" fontWeight={700} mb={0.5}>Start your free trial</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                No credit card required. Full access for {' '}
                <strong>7 days</strong>.
              </Typography>

              <Stack spacing={2}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      label="First name" fullWidth required
                      value={form.firstName} onChange={set('firstName')}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Last name" fullWidth
                      value={form.lastName} onChange={set('lastName')}
                    />
                  </Grid>
                </Grid>

                <TextField
                  label="Work email" fullWidth required type="email"
                  value={form.email} onChange={set('email')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment> }}
                />

                <TextField
                  label="Company name" fullWidth required
                  value={form.companyName} onChange={set('companyName')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Business fontSize="small" /></InputAdornment> }}
                />

                <TextField
                  select fullWidth label="Company size"
                  value={form.companySize} onChange={set('companySize')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">Select…</option>
                  {COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </TextField>

                <TextField
                  label="Password (optional)" fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={form.password} onChange={set('password')}
                  helperText="Set now or use a magic link to log in later"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.seedData}
                      onChange={e => setForm(f => ({ ...f, seedData: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Start with demo data
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pre-populate with sample products, warehouse, and inventory
                      </Typography>
                    </Box>
                  }
                />
              </Stack>

              {error && (
                <Typography variant="body2" color="error" mt={2}>{error}</Typography>
              )}

              <Button
                fullWidth variant="contained" size="large"
                sx={{ mt: 3, borderRadius: 2, py: 1.5 }}
                onClick={handleSubmitAccount}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Continue →'}
              </Button>

              {socialProviders.length > 0 && COGNITO_DOMAIN && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">or sign up with</Typography>
                  </Divider>

                  <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                    {socialProviders.map(p => (
                      <SocialButton
                        key={p.identityProvider}
                        provider={p.identityProvider}
                        label={p.displayName}
                        identityProvider={p.identityProvider}
                      />
                    ))}
                  </Stack>
                </>
              )}

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
                Already have an account?{' '}
                <Link href="/login">Sign in</Link>
              </Typography>
            </Box>
          )}

          {/* ── Step 1 — Email verification ── */}
          {step === 1 && (
            <Box textAlign="center">
              <Email sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} mb={1}>Check your email</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                We sent a 6-digit code to <strong>{form.email}</strong>.<br />
                Enter it below to confirm your account.
              </Typography>

              <TextField
                label="Verification code" fullWidth
                value={otp} onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontSize: 28, letterSpacing: 12 } }}
                sx={{ mb: 2 }}
              />

              {error && (
                <Typography variant="body2" color="error" mb={2}>{error}</Typography>
              )}

              <Button
                fullWidth variant="contained" size="large"
                sx={{ borderRadius: 2, py: 1.5 }}
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify & activate trial'}
              </Button>

              <Typography variant="body2" color="text.secondary" mt={2}>
                Didn&apos;t receive it?{' '}
                <Link href="#" onClick={e => { e.preventDefault(); handleResendOTP(); }}>
                  Resend code
                </Link>
              </Typography>
            </Box>
          )}

          {/* ── Step 2 — Workspace setup ── */}
          {step === 2 && (
            <Box>
              <CheckCircle sx={{ fontSize: 48, color: '#22c55e', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} mb={1}>Email verified!</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Your account is active. Here&apos;s what we&apos;re setting up:
              </Typography>

              <Stack spacing={1.5}>
                {[
                  { label: 'Tenant workspace created', done: true },
                  { label: 'All features enabled at default limits', done: true },
                  { label: 'Demo inventory & warehouse', done: form.seedData, pending: form.seedData },
                  { label: 'Demo API key generated', done: form.seedData, pending: form.seedData },
                ].map(item => (
                  <Stack key={item.label} direction="row" spacing={1.5} alignItems="center">
                    <CheckCircle sx={{ color: item.done ? '#22c55e' : '#d1d5db', fontSize: 20 }} />
                    <Typography variant="body2">{item.label}</Typography>
                    {item.pending && (
                      <Chip label="in progress" size="small" sx={{ ml: 'auto', bgcolor: '#eff6ff', color: '#1976d2' }} />
                    )}
                  </Stack>
                ))}
              </Stack>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                <Typography variant="body2" fontWeight={600} color="primary" mb={0.5}>
                  Your trial expires in 7 days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upgrade any time to keep your data and increase limits on only the features you need.
                </Typography>
              </Box>

              <Button
                fullWidth variant="contained" size="large"
                sx={{ mt: 3, borderRadius: 2, py: 1.5 }}
                onClick={handleWorkspaceNext}
              >
                Continue to my workspace →
              </Button>
            </Box>
          )}

          {/* ── Step 3 — Welcome / go to app ── */}
          {step === 3 && (
            <Box textAlign="center">
              <Typography variant="h5" fontWeight={700} mb={1}>
                Welcome, {form.firstName || 'there'}! 🎉
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                <strong>{form.companyName}</strong>&apos;s workspace is ready.
                Your 7-day trial starts now.
              </Typography>

              <Stack spacing={1} mb={3} textAlign="left">
                {[
                  'Explore your demo inventory and warehouse',
                  'Add your own products and locations',
                  'Generate API keys to connect your systems',
                  'Run reports to see usage trends',
                ].map(tip => (
                  <Stack key={tip} direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircle sx={{ color: '#22c55e', fontSize: 18, mt: '2px' }} />
                    <Typography variant="body2">{tip}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth variant="contained" size="large"
                sx={{ borderRadius: 2, py: 1.5 }}
                onClick={handleGoToApp}
              >
                Open Inventory Platform →
              </Button>

              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                A welcome email with next steps has been sent to {form.email}.
              </Typography>
            </Box>
          )}

        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary" mt={3}>
        By signing up you agree to our{' '}
        <Link href="/terms">Terms of Service</Link>
        {' '}and{' '}
        <Link href="/privacy">Privacy Policy</Link>.
      </Typography>
    </Box>
  );
}

/** Build the Cognito hosted-UI OAuth2 authorize URL for a given identity provider. */
function buildSsoUrl(identityProvider) {
  const redirectUri = `${APP_URL}/signup/callback`;
  // COGNITO_DOMAIN may already include https:// (the CFN export does)
  const domain = COGNITO_DOMAIN.replace(/^https?:\/\//, '');
  return (
    `https://${domain}/oauth2/authorize` +
    `?response_type=code` +
    `&client_id=${encodeURIComponent(USER_POOL_CLIENT_ID)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&identity_provider=${encodeURIComponent(identityProvider)}` +
    `&scope=openid+email+profile`
  );
}

const PROVIDER_ICONS = {
  Google: (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.06 24.06 0 0 0 0 21.56l7.98-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  ),
  Microsoft: (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  ),
  SignInWithApple: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.65-2.2.46-3.06-.4C3.79 16.17 4.36 9.04 8.9 8.78c1.27.06 2.15.72 2.9.76.93-.18 2.38-.92 3.63-.78 1.5.16 2.63.83 3.38 2.1-3.1 1.87-2.37 5.98.56 7.12-.67 1.76-1.52 3.5-3.32 5.32zM12.03 8.7c-.15-2.47 1.87-4.57 4.2-4.7.31 2.83-2.55 4.94-4.2 4.7z"/>
    </svg>
  ),
  Facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  LoginWithAmazon: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FF9900" d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.685zm3.186 7.705a.66.66 0 0 1-.753.077c-1.06-.876-1.25-1.281-1.829-2.115-1.748 1.784-2.985 2.318-5.249 2.318-2.68 0-4.764-1.654-4.764-4.96 0-2.583 1.399-4.339 3.392-5.2 1.727-.753 4.14-.886 5.98-1.094V6.39c0-.774.06-1.691-.395-2.36-.394-.6-1.156-.847-1.826-.847-1.242 0-2.349.637-2.62 1.958a.631.631 0 0 1-.546.545l-3.074-.33c-.258-.058-.544-.266-.47-.66C5.67 1.478 8.62.106 11.272.106c1.348 0 3.111.358 4.175 1.38 1.349 1.267 1.22 2.96 1.22 4.806v4.353c0 1.31.543 1.884 1.054 2.592.18.253.22.556-.01.744l-2.567 2.105v-.291z"/>
      <path fill="#FF9900" d="M21.768 18.633C19.132 20.678 15.24 21.75 11.903 21.75c-4.7 0-8.934-1.737-12.13-4.627-.25-.227-.027-.537.277-.361 3.455 2.008 7.73 3.216 12.142 3.216 2.978 0 6.254-.617 9.27-1.894.455-.195.836.298.306.549z"/>
    </svg>
  ),
};

function SocialButton({ provider, label, identityProvider }) {
  const href = COGNITO_DOMAIN ? buildSsoUrl(identityProvider) : '#';

  return (
    <Button
      variant="outlined" size="medium"
      href={href}
      data-testid={`sso-${provider}`}
      sx={{ borderRadius: 2, px: 2, textTransform: 'none', minWidth: 120 }}
      startIcon={PROVIDER_ICONS[provider]}
    >
      {label}
    </Button>
  );
}
